import { useState, useEffect } from "react";
import FullScreenModal from "@/components/FullScreenModal";
import { LIABILITY_CATEGORIES } from "@vaulzo/core/constants";
import { Loader2 } from "lucide-react";

export default function LiabilityDialog({
  isOpen,
  onClose,
  onSubmit,
  editingLiability = null,
  isSubmitting = false,
}) {
  const [category, setCategory] = useState(LIABILITY_CATEGORIES[0]);

  useEffect(() => {
    if (isOpen) setCategory(editingLiability?.category || LIABILITY_CATEGORIES[0]);
  }, [isOpen, editingLiability]);

  // Hand the parent a ready API payload; it only persists.
  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    onSubmit({
      name: fd.get("name"),
      category: fd.get("category"),
      amount: parseFloat(fd.get("amount")),
    });
  };

  return (
    <FullScreenModal open={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="w-full max-w-[520px]">
        <div className="mb-[14px] text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">Liability</div>
        <h2 className="m-0 mb-11 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">
          {editingLiability ? "Edit liability" : "Add liability"}
        </h2>

        <div className="flex flex-col gap-[30px]">
          {/* Name */}
          <label className="block">
            <span className="mb-[10px] block text-[13px] font-semibold text-vaulzo-muted">Name</span>
            <input
              name="name"
              required
              placeholder="e.g. Home loan"
              defaultValue={editingLiability?.name}
              className="w-full border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-base text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-danger"
            />
          </label>

          {/* Category chips */}
          <div className="block">
            <span className="mb-3 block text-[13px] font-semibold text-vaulzo-muted">Category</span>
            <div className="flex flex-wrap gap-2">
              {LIABILITY_CATEGORIES.map((c) => {
                const active = category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`rounded-full border-[1.5px] px-[15px] py-2 text-[13px] transition-colors ${
                      active
                        ? "border-vaulzo-danger bg-vaulzo-danger font-semibold text-vaulzo-on-fill"
                        : "border-vaulzo-chip bg-transparent font-medium text-vaulzo-muted hover:border-vaulzo-danger"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <input type="hidden" name="category" value={category} />
          </div>

          {/* Amount */}
          <label className="block">
            <span className="mb-[10px] block text-[13px] font-semibold text-vaulzo-muted">Amount owed</span>
            <div className="flex items-baseline gap-[10px] border-b-[1.5px] border-vaulzo-input-line focus-within:border-vaulzo-danger">
              <span className="text-[20px] text-vaulzo-fainter">₹</span>
              <input
                name="amount"
                type="number"
                step="0.01"
                required
                inputMode="numeric"
                defaultValue={editingLiability?.amount}
                placeholder="0"
                className="num flex-1 border-0 bg-transparent py-2 text-[20px] font-semibold text-vaulzo-ink outline-none placeholder:text-vaulzo-subtle"
              />
            </div>
          </label>
        </div>

        <div className="mt-[52px] flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-[11px] bg-vaulzo-row px-[26px] py-[14px] text-[15px] font-semibold text-vaulzo-muted transition-colors hover:bg-vaulzo-soft disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center rounded-[11px] bg-vaulzo-danger px-[30px] py-[14px] text-[15px] font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-danger-hover disabled:opacity-60"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : editingLiability ? (
              "Save changes"
            ) : (
              "Add liability"
            )}
          </button>
        </div>
      </form>
    </FullScreenModal>
  );
}
